import { useState, useEffect } from "react";
import { supabase } from "@/services/supabase";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "@/config/publicConfig";
import { useAuthStore } from "@/store/useAuthStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useProfileWalletQuery } from "@/hooks/queries/useProfileWalletQuery";

const ID_TYPES = [
  "在留卡 (Residence Card)",
  "驾照 (Driver's License)",
  "护照 (Passport)",
  "个人身份卡（マイナンバー）(MyNumber Card)",
  "中国居民身份证 (Chinese National ID)",
];

export default function AgeVerificationPage() {
  const { t } = useTranslation();
  const { user, profile: storeProfile, fetchProfile } = useAuthStore();
  const { data: pw, refetch: refetchProfileWallet } = useProfileWalletQuery(
    user?.id
  );
  const profile = pw?.profile ?? storeProfile;
  const navigate = useNavigate();

  const [selectedIdType, setSelectedIdType] = useState(ID_TYPES[0]); // Default to Residence Card
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsBackImage = !(
    selectedIdType.includes("护照") || selectedIdType.includes("MyNumber")
  );

  useEffect(() => {
    if (profile?.age_verified_status === "submitted") {
      // Already submitted, maybe show a message
    } else if (profile?.age_verified_status === "approved") {
      navigate("/app/profile");
    }
  }, [profile, navigate]);

  const handleSubmit = async () => {
    if (!user) return;
    if (!frontFile) {
      setError("请上传证件正面照片");
      return;
    }
    if (needsBackImage && !backFile) {
      setError("请上传证件反面照片");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("登录状态失效，请重新登录");

      const formData = new FormData();
      formData.append("document_type", selectedIdType);
      formData.append("front", frontFile);
      if (needsBackImage && backFile) {
        formData.append("back", backFile);
      }

      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/submit-identity-review`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_PUBLISHABLE_KEY,
          },
          body: formData,
        },
      );

      const bodyText = await res.text();
      if (!res.ok) {
        throw new Error(bodyText || `提交失败 (${res.status})`);
      }

      alert("证件提交成功！请等待管理员审核。");
      await refetchProfileWallet();
      await fetchProfile();
      navigate("/app/profile");
    } catch (err: any) {
      setError("提交失败: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  if (profile?.age_verified_status === "submitted") {
    return (
      <div className="max-w-md mx-auto p-8 bg-white rounded-xl shadow-lg mt-12 text-center">
        <div className="text-5xl mb-4">⏳</div>
        <h1 className="text-2xl font-bold mb-4">{t("verification.under_review")}</h1>
        <p className="text-gray-600">
          {t("verification.submission_notice")}
        </p>
        <button
          onClick={() => navigate("/app/profile")}
          className="mt-8 flex items-center text-pink-500 hover:text-pink-600 transition-colors"
        >
          <ArrowLeft size={20} className="mr-1" />
          {t("verification.back_to_profile")}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto p-8 bg-white rounded-xl shadow-lg mt-12">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        {t("verification.age_verification")}
      </h1>
      <p className="text-gray-500 mb-8">
        {t("verification.legal_notice")}
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t("verification.select_id_type")}
          </label>
          <select
            value={selectedIdType}
            onChange={(e) => setSelectedIdType(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
          >
            {ID_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              {t("verification.front_id")}
            </label>
            <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 h-40 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
              {frontFile ? (
                <img
                  src={URL.createObjectURL(frontFile)}
                  alt="Front"
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <span className="text-4xl mb-2 block">📇</span>
                  <span className="text-xs text-gray-400">{t("verification.click_to_upload_front")}</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFrontFile(e.target.files?.[0] || null)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>
          </div>

          {needsBackImage && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {t("verification.back_id")}
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 h-40 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                {backFile ? (
                  <img
                    src={URL.createObjectURL(backFile)}
                    alt="Back"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <span className="text-4xl mb-2 block">💳</span>
                    <span className="text-xs text-gray-400">{t("verification.click_to_upload_back")}</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setBackFile(e.target.files?.[0] || null)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
            ⚠️ {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={uploading}
          className="w-full py-4 bg-pink-600 text-white rounded-xl font-bold text-lg hover:bg-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-pink-100"
        >
          {uploading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {t("common.submitting")}
            </span>
          ) : (
            t("verification.submit_verification")
          )}
        </button>
      </div>
    </div>
  );
}
